import * as Sentry from '@sentry/react';
import { captureException } from '@sentry/react';
import { AxiosError } from 'axios';
import React from 'react';
import { createRoutesFromChildren, matchRoutes, Routes, useLocation, useNavigationType } from 'react-router-dom';
import { classifyError, ErrorClassification, shouldReport } from './errorClassification';

export { classifyError, shouldReport };
export type { ErrorClassification };

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'dev',
  dist: `${import.meta.env.VITE_SENTRY_ENVIRONMENT || 'dev'}-${import.meta.env.VITE_SENTRY_DIST || 'web'}`,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],

  tracePropagationTargets: [import.meta.env.VITE_APP_API_URL.replace(/^https?:\/\//, '')],

  // We recommend adjusting this value in production, or using tracesSampler
  // for finer control
  tracesSampleRate: import.meta.env.VITE_SENTRY_TRACE_RATE ? Number(import.meta.env.VITE_SENTRY_TRACE_RATE) : 0.1,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  normalizeDepth: 6,

  // Known browser/environment noise with no actionable signal for us. Chunk-load failures are
  // deliberately not included here - those mean a real deploy/CDN problem.
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    'Non-Error promise rejection captured',
    'Script error.',
  ],
});

export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

/**
 * The one place application code should call to report a caught error to Sentry. Always leaves a
 * breadcrumb (so even a suppressed 'expected' error shows up as context on whatever fires next);
 * additionally sends a full event unless `shouldReport(classification)` is false.
 */
export function reportError(
  error: Error,
  classification: ErrorClassification,
  extra?: { tags?: Record<string, string>; fingerprint?: string[] },
): string | undefined {
  Sentry.addBreadcrumb({
    category: 'error',
    level: classification === 'expected' || classification === 'external' ? 'warning' : 'error',
    message: error.message,
    data: { classification },
  });

  if (!shouldReport(classification)) {
    return undefined;
  }

  const contexts: Record<string, Record<string, unknown>> = {};
  if (error instanceof AxiosError) {
    contexts.axios = {
      method: error.config?.method || 'get',
      url: error.config?.url,
      status: error.response?.status,
      // TODO: request/response bodies can carry personal data (e.g. salary/bank/address). Kept for
      // debuggability for now - revisit if that tradeoff stops being worth it.
      data: error.config?.data,
      response: error.response?.data,
    };
  }

  return captureException(error, {
    contexts,
    level: classification === 'external' ? 'warning' : undefined,
    tags: { classification, ...extra?.tags },
    fingerprint: extra?.fingerprint,
  });
}
